import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { environment } from '../../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { User } from '../user/model/user';
import { AlertType, ModalComponent, ModalResult } from '@michelin/theme';
import { Group } from '../user/model/group';
import { UtilityService } from '../../../../shared/services/utility.service';
import { hasRolePermission, getLocationTypes, mapLanguage, mapLanguageCode } from '../../utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ADMIN_GROUP_NAME } from '../../../../shared/constants/constants';
import { UserManagementState } from '../../../../shared/state/UserManagementState';
import { Store } from '@ngxs/store';
import { PlantService } from '../../../../shared/services/plant.service';
import { SiteService } from '../../../../shared/services/site.service';
import { DealerState, UpdateGroupsAction, UpdatePlantsAction, UpdateSitesAction } from '../../../../shared/state/DealerState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { UserManagementService } from '../../services/user-management.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { DatePipe } from '@angular/common';
import { UserStateService } from '../../services/user-state.service';
import CustomValidators from '../PasswordMatcher';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { backOfficePermissions, functionList, generalPermissions, shopFloorPermissions } from '../shared/user-group-permissions';
import { BackOfficePermissions, PermissionsDetails } from '../user-group/model/UserGroupDetails';
import { UserFunctionLink } from '../user-group/model/UserFunction';
import { distinctUntilChanged, Observable } from 'rxjs';
import { UserTabService } from '../../services/user-tab.service';

@Component({
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
  standalone: false
})
export class UserInfoComponent implements OnInit, CanComponentDeactivate, OnDestroy, AfterViewInit {
  protected readonly ROLES = ROLES;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  @ViewChild('noCustomerAssignedModal', { static: true }) noCustomerAssignedModal: ModalComponent;
  @ViewChild('noCustomerAssignedWarning') noCustomerAssignedContentModal: TemplateRef<any>;

  @ViewChild('removeAssignedCustomersModal', { static: true }) removeAssignedCustomersModal: ModalComponent;
  @ViewChild('removeAssignedCustomersModalWarning') removeAssignedCustomersContentModal: TemplateRef<any>;

  @ViewChild('newPasswordInput') newPasswordInput: ElementRef;

  currentFocusInput = 'password';
  groups$: Observable<Array<Group>> = inject(Store).select(DealerState.groups);

  lastUpdateTime: string;
  createdTime: string;
  lastUpdatedBy: string;
  createdBy: string;
  customerToggle: string;
  initialFormValues: any;
  user: User;
  group: Group;
  loading = true;
  userFormGroup: FormGroup;
  username = '';
  userId = '';
  editMode = false;
  viewMode = 'view-component';
  locationTypes: string[] = [];
  locations: string[] = [];
  languages: string[] = [];
  dealer: Dealer;
  plantNames: string[] = [];
  siteNames: string[] = [];
  groupNames: string[] = [];
  isSaving = false;
  showRestrictCustomerAccessButton = false;
  bypassCanDeactivate = false;
  isAddMode: boolean;
  groupPermissionsLoading = false;
  shopFloorPermissions: PermissionsDetails[] = shopFloorPermissions;
  generalPermissions: PermissionsDetails[] = generalPermissions;
  backOfficePermissions: BackOfficePermissions[] = backOfficePermissions;
  functionList: UserFunctionLink[] = functionList;
  hideNew = true;
  hideConfirm = true;

  protected translationService = inject(TranslationService);
  protected plantService = inject(PlantService);
  protected SiteService = inject(SiteService);
  protected userManagementService = inject(UserManagementService);
  private readonly renderer = inject(Renderer2);
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly userStateService = inject(UserStateService);
  private readonly utilityService = inject(UtilityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly datePipe = inject(DatePipe);
  private readonly userTabService = inject(UserTabService);

  constructor() {
    this.activatedRoute.params.subscribe((params) => {
      this.userId = params['userId'];
    });
    this.userFormGroup = this.formBuilder.group({
      userInfo: this.formBuilder.group(
        {
          lastName: ['', Validators.required],
          firstName: ['', Validators.required],
          username: ['', Validators.required, this.noWhitespaceAndSpecialCharactersValidator()],
          selectedUserGroup: ['', Validators.required],
          active_Flg: [true],
          salesman: [false],
          selectedLocationType: ['', Validators.required],
          selectedLocation: ['', Validators.required],
          selectedLanguage: ['', Validators.required],
          btmId: [''],
          restrictCustomerAccess: [''],
          password: ['', Validators.required, CustomValidators.containsSpecialRequirementsValidator()],
          confirmPassword: ['', Validators.compose([Validators.required])]
        },
        {
          validators: [CustomValidators.match('password', 'confirmPassword')]
        }
      ),
      userPermissionInfo: this.formBuilder.group({
        shopfloorPostsPermissions: this.formBuilder.group({}),
        generalPermissions: this.formBuilder.group({}),
        backofficePermissions: this.formBuilder.group({})
      })
    });
    this.userFormGroup
      .get('userInfo')
      .get('selectedLocationType')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((locationType) => {
        if (locationType) {
          this.onSelectionChange('selectedLocationType', 'locationType', { value: locationType });
        }
      });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.isAddMode = params['mode'] === 'add';
      if (this.isAddMode) {
        this.userFormGroup.get('userInfo.restrictCustomerAccess')?.disable();
        // Initialize the form with empty fields for adding a new user
        this.userFormGroup.patchValue({
          userInfo: {
            lastName: '',
            firstName: '',
            username: '',
            selectedUserGroup: '',
            active_Flg: true,
            salesman: false,
            selectedLocationType: '',
            selectedLocation: '',
            selectedLanguage: '',
            btmId: '',
            restrictCustomerAccess: false,
            password: '',
            confirmPassword: ''
          }
        });

        this.loading = false;
      }
    });

    if (!this.isAddMode) {
      // Remove required validators from password fields in edit mode
      this.userFormGroup.get('userInfo.password').clearValidators();
      this.userFormGroup.get('userInfo.confirmPassword').clearValidators();
    }

    this.userFormGroup
      .get('userInfo')
      .get('selectedUserGroup')
      .valueChanges.subscribe((selectedUserGroup) => {
        this.fetchGroupDetails(selectedUserGroup);
      });
    this.customerToggle = 'N';

    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.editMode = this.canEditUserGroupAndSetup(ROLES.edit_user);
    this.viewMode = this.editMode ? 'edit-component' : 'view-component';
    this.languages = this.translationService.getSupportedLanguages();
    this.locationTypes = getLocationTypes();
    if (!this.isAddMode) {
      this.fetchUserDetails();
      this.fetchGroupDetails(this.user?.userGroupName);
    }
    Promise.all([this.fetchPlants(), this.fetchSites(), this.fetchUserGroups()]).then(() => {
      this.plantNames.unshift('');
      this.siteNames.unshift('');
      this.populateLocations();
      this.populateUserInfoForm();
      this.loading = false;
      this.initialFormValues = this.userFormGroup.value;
      const savedFormState = this.userStateService.getUserForm();
      if (savedFormState) {
        this.userFormGroup.setValue(savedFormState.value);
        this.populateLocations();
      }
      this.showRestrictCustomerAccessButton = this.userFormGroup.get('userInfo').get('restrictCustomerAccess')?.value;
    });
  }

  ngAfterViewInit() {
    // Only add event listeners if the element exists
    if (this.newPasswordInput) {
      this.renderer.listen(this.newPasswordInput.nativeElement, 'focus', () => {
        this.currentFocusInput = 'password';
      });
      // Note: You have a duplicate listener with an empty callback that can be removed
    }
  }

  fetchPlants(): Promise<void> {
    return new Promise((resolve) => {
      const plants = this.store.selectSnapshot(DealerState.plants);
      if (plants && plants.length > 0) {
        this.plantNames = plants.map((plant) => `${plant.code}-${plant.name}`);
        resolve();
      } else {
        this.plantService.getPlants(this.dealer.id).subscribe((plants) => {
          this.store.dispatch(new UpdatePlantsAction(plants));
          this.plantNames = plants.map((plant) => `${plant.code}-${plant.name}`);
          resolve();
        });
      }
    });
  }

  fetchSites(): Promise<void> {
    return new Promise((resolve) => {
      const sites = this.store.selectSnapshot(DealerState.sites);
      if (sites && sites.length > 0) {
        this.siteNames = sites.map((site) => `${site.code}-${site.name}`);
        resolve();
      } else {
        this.SiteService.getSites(this.dealer.id).subscribe((sites) => {
          this.store.dispatch(new UpdateSitesAction(sites));
          this.siteNames = sites.map((site) => `${site.code}-${site.name}`);
          resolve();
        });
      }
    });
  }

  fetchUserGroups(): Promise<void> {
    return new Promise((resolve) => {
      let groups = null;
      this.groups$.subscribe((group) => {
        groups = group;
      });
      if (groups && groups.length > 0) {
        this.groupNames = groups.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME).map((group) => group.groupName);
        resolve();
      } else {
        this.userManagementService.getUserGroups(this.dealer.id).subscribe((groups) => {
          const filteredGroups = groups.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME);
          this.store.dispatch(new UpdateGroupsAction(filteredGroups));
          this.groupNames = filteredGroups.map((group) => group.groupName);
          resolve();
        });
      }
    });
  }

  populateLocations(): void {
    const userLocationType = this.user ? this.user.locationType : this.userFormGroup.get('userInfo').get('selectedLocationType').value;
    if (userLocationType === '') {
      this.locations = [''];
    } else if (userLocationType === 'PLANT') {
      this.locations = [...this.plantNames];
    } else if (userLocationType === 'SITE') {
      this.locations = [...this.siteNames];
    } else {
      this.locations = [];
    }
  }

  fetchUserDetails(): Promise<void> {
    const formatUtcDate = (timestamp: string | undefined): string => {
      return this.datePipe.transform(timestamp, "yyyy/MM/dd 'at' hh:mm a", 'UTC');
    };

    return new Promise((resolve) => {
      this.store.select(UserManagementState.currentPaginatedUsers).subscribe((users) => {
        this.user = users.find((user) => user.userId === this.userId);
        this.user = { ...this.user, language: mapLanguageCode(this.user?.language) };
        this.userId = this.user.userId;
        this.username = this.user.username;
        this.lastUpdateTime = formatUtcDate(this.user.last_Updt_Timestamp);
        this.createdTime = formatUtcDate(
          this.user.created_Dt_Timestamp ? this.user.created_Dt_Timestamp : this.translationService.translate('defaultCreatedTimeText')
        );
        this.lastUpdatedBy = this.user.updatedBy;
        this.createdBy = this.user.createdBy ? this.user.createdBy : this.translationService.translate('defaultCreatedText');
        this.customerToggle = this.user.customerToggle;
        resolve();
      });
    });
  }

  populateUserInfoForm(): void {
    this.userFormGroup.patchValue({
      userInfo: {
        lastName: this.user?.lastName,
        firstName: this.user?.firstName,
        username: this.user?.username,
        selectedUserGroup: this.user?.userGroupName,
        active_Flg: this.user ? this.user.active_Flg !== 'N' : true,
        salesman: this.user?.salesman === 'Y',
        selectedLocationType: this.user?.locationType,
        selectedLocation: this.username ? this.user?.location : '',
        selectedLanguage: this.user?.language,
        btmId: this.user?.btmId ? this.user?.btmId : '',
        restrictCustomerAccess: this.user?.customerToggle === 'Y'
      }
    });
    if (!this.editMode && !this.isAddMode) {
      this.userFormGroup.disable();
    }
  }

  canEditUserGroupAndSetup(role: string): boolean {
    const roles: string = localStorage.getItem('roles');
    return roles.includes(role);
  }

  get userInfoFormGroup() {
    return this.userFormGroup.get('userInfo') as FormGroup;
  }

  onInputChange(type: string, field: string, event: any): void {
    if (type === 'userInfo') {
      if (!this.user) {
        this.user = {} as User;
      }
      this.user[field] = event.value;
      const userInfoPatch = {};
      userInfoPatch[field] = event.value;
      this.userFormGroup.get('userInfo').patchValue(userInfoPatch);
    }
  }

  onSelectionChange(formField: string, field: string, event: any): void {
    this.user = {
      ...this.user,
      [field]: event.value
    };
    if ('locationType' === field) {
      if ('' === event.value) {
        this.locations = [''];
      } else if ('PLANT' === event.value) {
        this.locations = this.plantNames;
      } else {
        this.locations = this.siteNames;
      }
      this.userFormGroup.patchValue({
        userInfo: {
          location: this.locations
        }
      });
    }
    this.userFormGroup.patchValue({
      userInfo: {
        [formField]: event.value
      }
    });
  }

  async saveUser() {
    this.userFormGroup.markAllAsTouched();

    if (this.userFormGroup.valid) {
      // Check if there are any modifications before saving
      if (!this.checkIfModified()) {
        this.utilityService.showAlert(AlertType.strong, '', this.translationService.translate('noModifiedText'));
        return;
      }

      console.log('Saving user details');
      this.isSaving = true;
      const userInfo = this.userFormGroup.get('userInfo').value;
      const loggedInUser = this.store.selectSnapshot(DealerState.currentUser);

      const payload: any = {
        userId: this.userId !== null && this.userId !== undefined && this.userId !== '' ? this.userId : '',
        username: userInfo.username,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        location: userInfo.selectedLocation,
        locationType: userInfo.selectedLocationType,
        language: mapLanguage(userInfo.selectedLanguage),
        customerToggle: this.showRestrictCustomerAccessButton ? 'Y' : 'N',
        active_Flg: userInfo.active_Flg ? 'Y' : 'N',
        salesman: userInfo.salesman ? 'Y' : 'N',
        userGroupId: this.mapUserGroupId(userInfo.selectedUserGroup),
        customers: this.showRestrictCustomerAccessButton
          ? this.userStateService.getAssignedCustomers().length > 0
            ? this.userStateService.getAssignedCustomers().map((customer) => customer.customerPk)
            : this.user && Array.isArray(this.user.customers) && this.user.customers.length > 0
            ? this.user.customers
            : []
          : [],
        updatedBy: loggedInUser,
        createdBy: loggedInUser
      };

      if (this.isAddMode) {
        payload.password = await this.authenticationService.encryptPassword(userInfo.password, this.dealer.newHashEnabled);
        if (environment.envName !== 'dev') {
          gtag('event', 'add_user_save_clicked', {
            event_category: 'BUTTON_CLICK',
            event_label: 'Add User Save Click',
            value: `Save User Add Button Clicked: ${userInfo.username}`
          });
        }
      } else {
        if (environment.envName !== 'dev') {
          gtag('event', 'update_user_clicked', {
            event_category: 'BUTTON_CLICK',
            event_label: 'Update Existing User Click',
            value: `User Update Button Clicked: ${userInfo.username}`
          });
        }
      }

      this.userManagementService.updateUser(this.dealer.id, this.username, payload).subscribe({
        next: (response) => {
          this.isSaving = false;
          let successMessage = this.userId
            ? this.translationService.translate('camSaveSuccessText')
            : this.translationService.translate('userCreatedSuccessfullyText');

          this.utilityService.showAlert(AlertType.success, '', successMessage);
          this.initialFormValues = this.userFormGroup.value;
          this.userId = response.userId;
          this.lastUpdateTime = this.datePipe.transform(response.last_Updt_Timestamp, "yyyy/MM/dd 'at' hh:mm a", 'UTC');
          this.createdTime = this.datePipe.transform(response.created_Dt_Timestamp, "yyyy/MM/dd 'at' hh:mm a", 'UTC');
          this.lastUpdatedBy = response.updatedBy;
          this.createdBy = response.createdBy;
          if (payload.customerToggle !== 'Y') {
            this.userStateService.resetSelectedCustomer();
            if (this.user && Array.isArray(this.user.customers) && this.user.customers.length > 0) {
              this.user.customers = [];
            }
          }
        },
        error: (error) => {
          this.isSaving = false;
          if (error.status === 409) {
            this.userInfoFormGroup.get('username').setErrors({ usernameExists: true });
          } else {
            let errorMessage = this.userId
              ? this.translationService.translate('userUpdateFailedText')
              : this.translationService.translate('userCreationFailedText');
            this.utilityService.showAlert(AlertType.critical, '', errorMessage);
          }
        }
      });
    }
  }

  trackByString(index: number, item: string): string {
    return item;
  }

  noWhitespaceAndSpecialCharactersValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
      const hasWhitespace = (control.value || '').indexOf(' ') >= 0;
      const hasSpecialCharacters = /[^a-zA-Z0-9]/.test(control.value);
      return Promise.resolve(hasWhitespace || hasSpecialCharacters ? { whitespaceOrSpecialCharacters: true } : null);
    };
  }

  onSlideToggleButtonChange(event: MatSlideToggleChange): void {
    if (!this.isAddMode) {
      this.user.active_Flg = event.checked ? 'Y' : 'N';
    }
    this.userFormGroup.patchValue({
      userInfo: {
        active_Flg: event.checked
      }
    });
  }

  onSalesmanToggleButtonChange(event: MatSlideToggleChange): void {
    if (!this.user) {
      this.user = {} as User;
    }
    this.user.salesman = event.checked ? 'Y' : 'N';
    this.userFormGroup.patchValue({
      userInfo: {
        salesman: event.checked
      }
    });
  }

  onRestrictCustomerAccessToggle(event: MatSlideToggleChange): void {
    this.showRestrictCustomerAccessButton = event.checked;
    this.customerToggle = event.checked ? 'Y' : 'N';
  }

  private isFormChanged(): boolean {
    return JSON.stringify(this.initialFormValues) !== JSON.stringify(this.userFormGroup.value);
  }

  /**
   * Checks if any modifications have been made to the user data
   * including form fields, customer toggle, and assigned customers
   */
  private checkIfModified(): boolean {
    // Check if form values have changed
    if (this.isFormChanged()) {
      return true;
    }

    // Check if customer toggle state has changed
    const currentCustomerToggle = this.showRestrictCustomerAccessButton ? 'Y' : 'N';
    if (!this.isAddMode && this.user && currentCustomerToggle !== this.user.customerToggle) {
      return true;
    }

    // Check if assigned customers have changed
    if (this.showRestrictCustomerAccessButton) {
      const currentAssignedCustomers = this.userStateService.getAssignedCustomers().map((customer) => customer.customerPk);
      const originalCustomers = this.user && Array.isArray(this.user.customers) ? this.user.customers : [];

      // Compare customer arrays
      if (currentAssignedCustomers.length !== originalCustomers.length) {
        return true;
      }

      // Check if all customers are the same (order-independent comparison)
      const currentSet = new Set(currentAssignedCustomers);
      const hasChanges = originalCustomers.some((customer) => !currentSet.has(customer));
      if (hasChanges) {
        return true;
      }
    }

    return false;
  }

  private mapUserGroupId(userGroupName: string): string {
    const groups = this.store.selectSnapshot(DealerState.groups);
    if (!groups) {
      return null;
    }
    return groups.find((group) => group.groupName === userGroupName)?.userGroupId;
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.bypassCanDeactivate) {
      return canDeactivateForm(
        this.isFormChanged.bind(this),
        this.unsavedChangesModal,
        this.unsavedChangesContentModal,
        this.translationService,
        () => {
          this.userTabService.resetTab(0);
          return false;
        }
      );
    } else {
      return true;
    }
  }

  openSelectCustomerScreen() {
    // log the customer in userstate
    console.log('Customer in user state:', this.userStateService.getAssignedCustomers());
    this.userStateService.setUserForm(this.userFormGroup);
    const userId = this.userId ? this.userId : 'newUser';
    // Pass the mode query param when navigating
    this.router.navigate([`/home/setup/usersetup/user/${userId}/select-customer`], {
      queryParams: { mode: this.isAddMode ? 'add' : 'edit' },
      replaceUrl: true
    });
    this.bypassCanDeactivate = true;
  }

  resetPermissions() {
    this.shopFloorPermissions.forEach((permission) => {
      permission.isCheckedLeftBox = false;
      permission.isCheckedRightBox = false;
    });

    this.generalPermissions.forEach((permission) => {
      permission.isCheckedLeftBox = false;
      permission.isCheckedRightBox = false;
    });

    this.backOfficePermissions.forEach((permission) => {
      permission.isViewChecked = false;
      permission.isEditChecked = false;
      permission.isCreateChecked = false;
      permission.isAllChecked = false;
    });
  }

  ngOnDestroy() {
    if (!this.bypassCanDeactivate) {
      this.userStateService.resetSelectedCustomer();
      this.userStateService.resetState();
      this.resetPermissions();
    }
  }

  validateUserDetailsAndSaveChanges(): void {
    this.userFormGroup.markAllAsTouched();
    if (this.userFormGroup.valid) {
      if (this.userFormGroup.get('userInfo').get('restrictCustomerAccess')?.value && !this.assignedCustomersPresent()) {
        this.showNoCustomerAssignedModal();
      } else if (
        !this.userFormGroup.get('userInfo').get('restrictCustomerAccess')?.value &&
        this.userFormGroup.get('userInfo').get('active_Flg')?.value &&
        !this.isAddMode &&
        this.assignedCustomersPresent()
      ) {
        this.showRemoveAssignedCustomersModal();
      } else {
        this.saveUser();
      }
    } else {
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('incorrectValuesOnUserText')
      );
    }
  }

  assignedCustomersPresent(): boolean {
    const assignedCustomers = this.userStateService.getAssignedCustomers();
    if (assignedCustomers && assignedCustomers.length > 0) {
      return true;
    }
    return this.user && Array.isArray(this.user.customers) && this.user.customers.length > 0;
  }

  onNoCustomerAssignedModalClosed(result: ModalResult) {
    if (result.result) {
      this.customerToggle = 'N'; // Update the customer toggle if save occurs with no customers are selected
      // Properly update the form with patchValue
      this.userFormGroup.get('userInfo').patchValue({
        restrictCustomerAccess: false
      });
      this.showRestrictCustomerAccessButton = false; // Update UI state
      this.saveUser();
    }
  }

  onRemoveAssignedCustomersModalClosed(result: ModalResult) {
    if (result.result) {
      this.saveUser();
    } else {
      this.customerToggle = 'Y';
      this.userFormGroup.get('userInfo').patchValue({
        restrictCustomerAccess: true
      });
      this.showRestrictCustomerAccessButton = true;
    }
  }

  showNoCustomerAssignedModal(): void {
    this.noCustomerAssignedModal.open({
      id: 'no-customer-assigned-warning',
      width: 600,
      title: this.translationService.translate('warningText'),
      content: this.noCustomerAssignedContentModal,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
  }

  showRemoveAssignedCustomersModal(): void {
    this.removeAssignedCustomersModal.open({
      id: 'remove-assigned-customers-warning',
      width: 600,
      title: this.translationService.translate('warningText'),
      content: this.removeAssignedCustomersContentModal,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
  }

  fetchGroupDetails(selectedUserGroup: string): void {
    // Skip processing if selectedUserGroup is empty or undefined
    if (!selectedUserGroup) {
      this.groupPermissionsLoading = false;
      return;
    }

    const userGroupId = this.mapUserGroupId(selectedUserGroup);

    // Skip API call if userGroupId couldn't be found
    if (!userGroupId) {
      this.groupPermissionsLoading = false;
      return;
    }

    this.userManagementService.getUserGroupDetails(this.dealer.id, this.mapUserGroupId(selectedUserGroup)).subscribe((response) => {
      if (response) {
        this.functionList = response.userFunctionLink;
        const keyList = this.getAllPermissionKeys();

        let isBackOfficeLoaded = false;
        let isShopFloorPermissionLoaded = false;
        let isGeneralPermissionLoaded = false;

        this.backOfficePermissions.forEach((permission, index) => {
          permission.isViewChecked = false;
          permission.isEditChecked = false;
          permission.isCreateChecked = false;
          permission.isAllChecked = false;
          this.functionList.forEach((userFunctionLink) => {
            if (userFunctionLink.classification === 'BACK_OFFICE' && userFunctionLink.functionName.includes(permission.leftSideLabelKey)) {
              if (this.isViewEditCreateEnabled(keyList, 'WORK_ORDER', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'WORK_ORDER_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'WORK_ORDER_EDIT');
                permission.isCreateChecked = this.isPermissionAvailable(keyList, 'WORK_ORDER_CREATE');
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked && permission.isCreateChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'POS', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'VIEW_POS_INTERFACES');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'EDIT_POS_INTERFACES');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'USER_SETUP', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'USER_SETUP_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'USER_SETUP_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'PROFILE_SETUP', userFunctionLink.functionName)) {
                permission.isAllChecked = this.isPermissionAvailable(keyList, 'PROFILE_SETUP');
                permission.isEditChecked = permission.isAllChecked;
                permission.isCreateChecked = permission.isAllChecked;
                permission.isViewChecked = permission.isAllChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'CUSTOMER', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'CUSTOMER_SETUP_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'CUSTOMER_SETUP_EDIT');
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'SITE_SETUP', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'SITE_SETUP_INFO_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'SITE_SETUP_INFO_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'PLANT_SETUP', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'PLANT_SETUP_INFO_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'PLANT_SETUP_INFO_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'SETUP_SERVICES', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'SETUP_SERVICES_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'SETUP_SERVICES_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'PRODUCT_SETUP', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'PRODUCT_SETUP_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'PRODUCT_SETUP_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'SETUP_FLEET_SPECIFICATIONS', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'SETUP_FLEET_SPECIFICATIONS_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'SETUP_FLEET_SPECIFICATIONS_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              } else if (this.isViewEditCreateEnabled(keyList, 'SETUP_CASING_GRADE', userFunctionLink.functionName)) {
                permission.isViewChecked = this.isPermissionAvailable(keyList, 'SETUP_CASING_GRADE_VIEW');
                permission.isEditChecked = this.isPermissionAvailable(keyList, 'SETUP_CASING_GRADE_EDIT');
                permission.isCreateChecked = false;
                permission.isAllChecked = permission.isViewChecked && permission.isEditChecked;
              }
            }
          });

          if (index === this.backOfficePermissions.length - 1) {
            isBackOfficeLoaded = true;
          }
        });
        this.shopFloorPermissions.forEach((permission, index) => {
          permission.isCheckedLeftBox = false;
          permission.isCheckedRightBox = false;
          this.functionList.forEach((userFunctionLink) => {
            if (userFunctionLink.functionName.includes(permission.leftSideLabelKey)) {
              permission.isCheckedLeftBox = true;
            }
            if (userFunctionLink.functionName.includes(permission.rightSideLabelKey)) {
              permission.isCheckedRightBox = true;
            }
          });
          if (index === this.shopFloorPermissions.length - 1) {
            isShopFloorPermissionLoaded = true;
          }
        });
        this.generalPermissions.forEach((permission, index) => {
          permission.isCheckedLeftBox = false;
          permission.isCheckedRightBox = false;
          this.functionList.forEach((userFunctionLink) => {
            if (permission.leftSideLabelKey === userFunctionLink.functionName) {
              permission.isCheckedLeftBox = true;
            }
            if (permission.rightSideLabelKey === userFunctionLink.functionName) {
              permission.isCheckedRightBox = true;
            }
          });
          if (index === this.generalPermissions.length - 1) {
            isGeneralPermissionLoaded = true;
          }
        });
        if (isBackOfficeLoaded && isShopFloorPermissionLoaded && isGeneralPermissionLoaded) {
          this.groupPermissionsLoading = false;
        }
      }
    });
  }

  getAllPermissionKeys(): string[] {
    const keys: string[] = [];
    this.functionList.forEach((permission) => {
      keys.push(permission.functionName);
    });
    return keys;
  }

  isViewEditCreateEnabled(permission: string[], key: string, functionKey: string) {
    return this.isPermissionAvailable(permission, key) && functionKey.includes(key);
  }

  isPermissionAvailable(permission: string[], matchVal: string) {
    for (const element of permission) {
      if (element.includes(matchVal)) {
        return true;
      }
    }
    return false;
  }

  toggleRestrictCustomerAccess(): void {
    const userInfo = this.userFormGroup.get('userInfo');

    if (userInfo?.valid) {
      userInfo.get('restrictCustomerAccess')?.enable();
    } else {
      userInfo.get('restrictCustomerAccess')?.disable();
    }
  }

  checkMinLength(fieldName: string) {
    if (
      this.userFormGroup.get('userInfo').get(fieldName).value.length < 10 &&
      this.userFormGroup.get('userInfo').get(fieldName).value !== ''
    ) {
      this.userFormGroup.get('userInfo').get(fieldName).markAsTouched();
      return 'red';
    }
    return this.userFormGroup.get('userInfo').get(fieldName).value === '' ? '#7f7f7f' : 'green';
  }

  checkPasswordCriteriaUpper(password: string) {
    if (/[A-Z]/.test(this.userFormGroup.get('userInfo').get(password).value)) {
      return 'green';
    } else if (
      !this.userFormGroup.get('userInfo').get(password).hasError('containsSpecialRequirements') &&
      this.userFormGroup.get('userInfo').get(password).value !== ''
    ) {
      this.userFormGroup.get('userInfo').get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaLower(password: string) {
    if (/[a-z]/.test(this.userFormGroup.get('userInfo').get(password).value)) {
      return 'green';
    } else if (
      !this.userFormGroup.get('userInfo').get(password).hasError('containsSpecialRequirements') &&
      this.userFormGroup.get('userInfo').get(password).value !== ''
    ) {
      this.userFormGroup.get('userInfo').get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaNumber(password: string) {
    if (/\d/.test(this.userFormGroup.get('userInfo').get(password).value)) {
      return 'green';
    } else if (
      !this.userFormGroup.get('userInfo').get(password).hasError('containsSpecialRequirements') &&
      this.userFormGroup.get('userInfo').get(password).value !== ''
    ) {
      this.userFormGroup.get('userInfo').get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaSpecial(password: string) {
    if (/[~`!@#$%^&*()\-_=+{}[\]|\\;:"<>,./?]/.test(this.userFormGroup.get('userInfo').get(password).value)) {
      return 'green';
    } else if (
      !this.userFormGroup.get('userInfo').get(password).hasError('containsSpecialRequirements') &&
      this.userFormGroup.get('userInfo').get(password).value !== ''
    ) {
      this.userFormGroup.get('userInfo').get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  protected readonly hasRolePermission = hasRolePermission;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }
}
